import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jobsApi, ApiError } from "@/api";
import type { JobCreateInput, JobClientPickerItem, JobStatus } from "@/types";
import { JOB_STATUSES } from "@/types";
import { Button, TextField, TextAreaField, SelectField } from "@/components/ui";
import { jobStatusLabel } from "@/lib/jobs";
import { FormDrawer } from "./FormDrawer";

type ClientMode = "existing" | "individual" | "company";

const MODES: { value: ClientMode; label: string }[] = [
  { value: "existing", label: "Existing client" },
  { value: "individual", label: "New person" },
  { value: "company", label: "New company" },
];

/** New Job drawer (brief §3.6). Lead client via one of three paths; on success
 *  navigates to the new job, passing a flash banner (incl. a soft Xero note when
 *  a newly-created client couldn't sync). */
export function NewJobDrawer({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();

  const [jobName, setJobName] = useState("");
  const [address, setAddress] = useState("");
  const [postcode, setPostcode] = useState("");
  const [status, setStatus] = useState<JobStatus>("not_started");
  const [notes, setNotes] = useState("");

  const [mode, setMode] = useState<ClientMode>("existing");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");

  // Existing-client picker.
  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState<JobClientPickerItem[]>([]);
  const [picked, setPicked] = useState<JobClientPickerItem | null>(null);
  const [searching, setSearching] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Debounced type-ahead. Skip while a client is already picked (the input then
  // shows the chosen client; clearing it re-opens the search).
  useEffect(() => {
    if (mode !== "existing" || picked) return;
    const term = clientQuery.trim();
    if (!term) {
      setClientResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(() => {
      jobsApi
        .listJobClients(term)
        .then((rows) => {
          if (!cancelled) setClientResults(rows ?? []);
        })
        .catch(() => {
          if (!cancelled) setClientResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [clientQuery, mode, picked]);

  function validate(): string | null {
    if (mode === "existing") {
      if (!picked) return "Choose an existing client, or switch to creating a new one.";
    } else if (mode === "individual") {
      if (!first.trim() || !last.trim()) return "Enter the client's first and last name.";
    } else if (mode === "company") {
      if (!company.trim()) return "Enter the company name.";
    }
    return null;
  }

  async function submit() {
    if (saving) return;
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError(undefined);

    const input: JobCreateInput = {
      job_name: jobName.trim() || undefined,
      full_address: address.trim() || undefined,
      postcode: postcode.trim() || undefined,
      status,
      notes: notes.trim() || undefined,
    };
    if (mode === "existing" && picked) {
      input.client_id = picked.id;
    } else if (mode === "individual") {
      input.contact_first_name = first.trim();
      input.contact_last_name = last.trim();
      input.email = email.trim() || undefined;
    } else if (mode === "company") {
      input.is_company = true;
      input.company_name = company.trim();
      input.email = email.trim() || undefined;
    }

    try {
      const res = await jobsApi.createJob(input);
      const flash =
        res.client_was_created && !res.xero_synced
          ? { kind: "warn" as const, message: "Job created — but the new client couldn't be synced to Xero. Add them in Xero manually if needed." }
          : { kind: "info" as const, message: `Job #${res.job_number} created.` };
      navigate(`/staff/jobs/${res.id}`, { state: { flash } });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create the job.");
      setSaving(false);
    }
  }

  return (
    <FormDrawer
      title="New job"
      busy={saving}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={saving}>Create job</Button>
        </>
      }
    >
      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      <section className="job-drawer__section">
        <h3>Job</h3>
        <TextField label="Job name" value={jobName} onChange={(e) => setJobName(e.target.value)} placeholder="e.g. 14 Elm Road" />
        <TextField label="Full address" value={address} onChange={(e) => setAddress(e.target.value)} />
        <TextField label="Postcode" value={postcode} onChange={(e) => setPostcode(e.target.value.toUpperCase())} />
        <SelectField label="Status" value={status} onChange={(e) => setStatus(e.target.value as JobStatus)}>
          {JOB_STATUSES.map((s) => (
            <option key={s} value={s}>{jobStatusLabel(s)}</option>
          ))}
        </SelectField>
        <TextAreaField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </section>

      <section className="job-drawer__section">
        <h3>Lead client</h3>
        <div className="job-seg" role="tablist" aria-label="Lead client type">
          {MODES.map((m) => (
            <button
              key={m.value}
              type="button"
              role="tab"
              aria-selected={mode === m.value}
              className={`job-seg__btn${mode === m.value ? " is-on" : ""}`}
              onClick={() => { setMode(m.value); setError(undefined); }}
            >
              {m.label}
            </button>
          ))}
        </div>

        {mode === "existing" ? (
          <div className="job-client-picker">
            {picked ? (
              <div className="job-client-picked">
                <div>
                  <strong>{picked.display_name}</strong>
                  {picked.email ? <span className="job-drawer__muted"> · {picked.email}</span> : null}
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setPicked(null); setClientQuery(""); }}>Change</Button>
              </div>
            ) : (
              <>
                <input
                  className="leads__search"
                  type="search"
                  placeholder="Search existing clients by name or email…"
                  value={clientQuery}
                  onChange={(e) => setClientQuery(e.target.value)}
                />
                {searching ? <p className="job-drawer__muted">Searching…</p> : null}
                {!searching && clientQuery.trim() && clientResults.length === 0 ? (
                  <p className="job-drawer__muted">No clients found — try a different term, or create a new client.</p>
                ) : null}
                {clientResults.length > 0 ? (
                  <ul className="job-client-results">
                    {clientResults.map((c) => (
                      <li key={c.id}>
                        <button type="button" className="job-client-result" onClick={() => { setPicked(c); setClientResults([]); }}>
                          <span className="job-client-result__name">{c.display_name}</span>
                          {c.email ? <span className="job-client-result__email">{c.email}</span> : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </>
            )}
          </div>
        ) : mode === "individual" ? (
          <>
            <div className="lead-drawer__two">
              <TextField label="First name" required value={first} onChange={(e) => setFirst(e.target.value)} />
              <TextField label="Last name" required value={last} onChange={(e) => setLast(e.target.value)} />
            </div>
            <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </>
        ) : (
          <>
            <TextField label="Company name" required value={company} onChange={(e) => setCompany(e.target.value)} />
            <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </>
        )}
      </section>
    </FormDrawer>
  );
}
